import React, { useState } from 'react';
import { Box, Button, Header, Pagination, SpaceBetween, StatusIndicator, Table } from '@amzn/awsui-components-react';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import PropertyFilter from '@amzn/awsui-components-react/polaris/property-filter';
import { useCollection } from '@amzn/awsui-collection-hooks';
import DateUtils from 'src/utils/dateUtils';
import CopyToClipboard from 'src/components/CopyToClipboard';
import { BulkPromotionSummary } from 'src/models/promotion/BulkPromotionTypes';
import { readBatchCounts, readBatchId, readBatchStatus } from 'src/components/calculations/bulk-promote/bulkPromotionRecord';
import { PromotionComponentType } from 'src/models/promotion/PromotionTypes';
import { getBatchStatusBand } from 'src/components/calculations/bulk-promote/bulkPromotionBands';
import { formatIdsForClipboard } from 'src/components/calculations/bulk-promote/bulkIdentifierClientParser';
import { getBulkPromoteTypeLabel } from 'src/models/promotion/bulkPromotionConstants';
import {
    BULK_PROMOTION_EXPANDED_ID_LIMIT,
    BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS,
    BULK_PROMOTION_HISTORY_FILTERING_PROPERTIES,
    BULK_PROMOTION_HISTORY_PAGE_SIZE_OPTIONS,
    BULK_PROMOTION_HISTORY_VISIBLE_CONTENT_OPTIONS,
} from './bulkPromotionHistoryConstants';
import {
    PROPERTY_FILTERING_I18N_CONSTANTS,
    PAGINATION_LABELS,
    getFilterCounterText,
    getTablePreferences,
} from '../shared/TPPolarisTable';

const PROMOTED_IDS_CHILD_MARKER = '__PROMOTED_IDS__';

export interface BulkPromotionHistoryRow extends BulkPromotionSummary {
    createdAtDisplay: string;
    statusDisplay: string;
    componentTypeDisplay: string;
    promotedByDisplay: string;
    promotedIds?: string[];
}

/**
 * Normalises one page of the list query into rows, and is the only place that knows two spellings of a
 * batch are in play.
 *
 * GetBulkPromotionsComponent.toSummary emits the Smithy spelling — `batchId`, `status`, and the five
 * counters nested under `counts` — while every column id, sortingField and the table's trackBy below
 * read the spellings declared in BulkPromotionTypes: bulkPromotionId, bulkPromotionStatus and five
 * flat *Count fields. Read under the wrong name, all seven are undefined, and that is not a cosmetic
 * blank: trackBy collapses to one key for every row, and isItemExpandable calls endsWith on the
 * missing id for every row, which throws and takes the whole tab down.
 *
 * Both spellings are accepted, through the same readBatch* helpers the resume path uses, so this keeps
 * working whichever side is renamed next.
 */
export function toBulkPromotionHistoryRows(records: BulkPromotionSummary[]): BulkPromotionHistoryRow[] {
    return records.map((record) => {
        const counts = readBatchCounts(record);
        const bulkPromotionId = readBatchId(record) || '';
        const bulkPromotionStatus = readBatchStatus(record) || '';
        return {
            ...record,
            bulkPromotionId,
            bulkPromotionStatus,
            requestedCount: counts.requested,
            promotedCount: counts.promoted,
            blockedCount: counts.blocked,
            failedCount: counts.failed,
            skippedCount: counts.skipped,
            createdAtDisplay: record.createdAt ? DateUtils.formatTimestamp(record.createdAt) : '',
            statusDisplay: getBatchStatusBand(bulkPromotionStatus)[0],
            componentTypeDisplay: getBulkPromoteTypeLabel(record.componentType),
            // The writer records a bare alias, so this suffix strip is a no-op against today's service.
            // It is kept as a tolerance rather than removed: a bare alias passes through unchanged, and
            // a fully-qualified one would otherwise widen the column and break the property filter's
            // value list. See the note in BulkPromotionHistoryTableTest.
            promotedByDisplay: (record.promotedBy || '').replace(/@amazon\.com$/, ''),
        };
    });
}

interface BulkPromotionHistoryTableProps {
    rows: BulkPromotionHistoryRow[];
    isLoading: boolean;
    hasNextPage: boolean;
    currentPageIndex: number;
    pagesCount: number;
    onNextPage: () => void;
    onPreviousPage: () => void;
    onPageChange: (pageIndex: number) => void;
    onRefresh: () => void;
    onExpandBatch: (bulkPromotionId: string) => void;
    preferences: any;
    onPreferencesChange: (prefs: any) => void;
}

/**
 * One row per batch. The list query returns counts only, so opening a row asks for that batch's
 * promoted ids on demand rather than assuming the row already carries them.
 */
export default function BulkPromotionHistoryTable(props: BulkPromotionHistoryTableProps) {
    const {
        rows,
        isLoading,
        hasNextPage,
        currentPageIndex,
        pagesCount,
        onNextPage,
        onPreviousPage,
        onPageChange,
        onRefresh,
        onExpandBatch,
        preferences,
        onPreferencesChange,
    } = props;

    const [expandedIds, setExpandedIds] = useState<string[]>([]);

    const columnDefinitions: TableProps.ColumnDefinition<BulkPromotionHistoryRow>[] = [
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.STARTED,
            cell: (item) => item.createdAtDisplay,
            isRowHeader: true,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.BATCH_ID,
            // Monospaced because these are opaque ids read character by character when comparing one
            // against a Step Functions execution name or a ticket.
            cell: (item) => <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{item.bulkPromotionId}</span>,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.STATUS,
            cell: (item) => {
                const [label, band] = getBatchStatusBand(item.bulkPromotionStatus);
                return <StatusIndicator type={band}>{label}</StatusIndicator>;
            },
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.COMPONENT_TYPE,
            cell: (item) => item.componentTypeDisplay,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.REQUESTED,
            cell: (item) => item.requestedCount,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.PROMOTED,
            cell: (item) => item.promotedCount,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.SKIPPED,
            cell: (item) => item.skippedCount,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.BLOCKED,
            cell: (item) => item.blockedCount,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.FAILED,
            cell: (item) => item.failedCount,
        },
        {
            ...BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.PROMOTED_BY,
            cell: (item) => item.promotedByDisplay,
        },
    ];

    const expandableRows: TableProps.ExpandableRows<BulkPromotionHistoryRow> = {
        getItemChildren: (item) => {
            if (!item.promotedIds || item.promotedIds.length === 0) return [];
            return [
                {
                    ...item,
                    bulkPromotionId: `${item.bulkPromotionId}${PROMOTED_IDS_CHILD_MARKER}`,
                    createdAtDisplay: PROMOTED_IDS_CHILD_MARKER,
                },
            ];
        },
        isItemExpandable: (item) => !item.bulkPromotionId.endsWith(PROMOTED_IDS_CHILD_MARKER) && item.promotedCount > 0,
        // Derived from ids rather than held as object references: loading a batch's ids replaces
        // its row object, which would leave a reference-keyed set unable to find it again.
        expandedItems: rows.filter((row) => expandedIds.includes(row.bulkPromotionId)),
        onExpandableItemToggle: ({ detail }) => {
            const batchId = detail.item.bulkPromotionId;
            if (detail.expanded) {
                onExpandBatch(batchId);
            }
            setExpandedIds((prev) =>
                detail.expanded ? [...prev, batchId] : prev.filter((id) => id !== batchId)
            );
        },
    };

    // Only promoted components are listed — never blocked, never failed. Those are reached from
    // the batch itself, where the reason and its remedy are available.
    columnDefinitions[0] = {
        ...columnDefinitions[0],
        cell: (item) => {
            if (item.createdAtDisplay !== PROMOTED_IDS_CHILD_MARKER) return item.createdAtDisplay;
            const promotedIds = item.promotedIds || [];
            const shown = promotedIds.slice(0, BULK_PROMOTION_EXPANDED_ID_LIMIT);
            return (
                <div className="promotedRowsDetail">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Box color="text-status-success" fontWeight="bold" fontSize="body-s">
                            Promoted components ({promotedIds.length}):
                        </Box>
                        <CopyToClipboard
                            textToBeCopied={formatIdsForClipboard(
                                promotedIds,
                                item.componentType as PromotionComponentType
                            )}
                            popoverMessage={`${promotedIds.length} IDs copied`}
                        />
                    </SpaceBetween>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {shown.map((id, i) => (
                            <div key={i}>{id}</div>
                        ))}
                    </div>
                    {promotedIds.length > shown.length && (
                        <Box variant="small">
                            and {promotedIds.length - shown.length} more — use Copy all to get the full list.
                        </Box>
                    )}
                </div>
            );
        },
    };

    for (let i = 1; i < columnDefinitions.length; i++) {
        const original = columnDefinitions[i].cell;
        columnDefinitions[i] = {
            ...columnDefinitions[i],
            cell: (item) => (item.createdAtDisplay === PROMOTED_IDS_CHILD_MARKER ? null : (original as any)(item)),
        };
    }

    const { items, collectionProps, propertyFilterProps, filteredItemsCount } = useCollection(rows, {
        propertyFiltering: {
            filteringProperties: BULK_PROMOTION_HISTORY_FILTERING_PROPERTIES,
        },
        sorting: {
            defaultState: {
                sortingColumn: { sortingField: BULK_PROMOTION_HISTORY_COLUMN_DEFINITIONS.STARTED.sortingField } as any,
                isDescending: true,
            },
        },
    });

    return (
        <Table
            {...collectionProps}
            columnDefinitions={columnDefinitions}
            items={items}
            header={
                <Header
                    actions={
                        <Button
                            iconName="refresh"
                            variant="normal"
                            loading={isLoading}
                            onClick={onRefresh}
                            ariaLabel="Refresh bulk promotion history">
                            Refresh
                        </Button>
                    }>
                    Bulk promotion history
                </Header>
            }
            loadingText="Loading bulk promotion history"
            loading={isLoading}
            resizableColumns={true}
            trackBy="bulkPromotionId"
            wrapLines={preferences.wrapLines}
            stripedRows={preferences.stripedRows}
            columnDisplay={preferences.contentDisplay}
            stickyColumns={preferences.stickyColumns}
            empty={
                <Box textAlign="center">
                    <b>No bulk promotions found for this period.</b>
                </Box>
            }
            variant="container"
            expandableRows={expandableRows}
            filter={
                /*
                 * The empty text says which rows were searched, because useCollection filters the `rows` it is
                 * given and the panel holds ONE page at a time — paging is server-side on nextToken. A filter
                 * on a batch id that lives on an unloaded page therefore matches nothing, and "no bulk
                 * promotions match the filter criteria" reads as "that batch does not exist" for a batch that
                 * does. Saying it searched this page is the honest claim until filtering moves to the service,
                 * which needs the list read to take a filter it does not take today.
                 *
                 * Sorting has the same scope, so the default newest-first is newest-within-this-page.
                 */
                <PropertyFilter
                    i18nStrings={{ ...PROPERTY_FILTERING_I18N_CONSTANTS, filteringPlaceholder: 'Find on this page' }}
                    {...propertyFilterProps}
                    countText={getFilterCounterText(filteredItemsCount)}
                    filteringEmpty="No matches on this page. Filtering searches the loaded page only — check later pages."
                    filteringLoadingText="Loading bulk promotion history..."
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={currentPageIndex}
                    pagesCount={pagesCount}
                    openEnd={hasNextPage}
                    ariaLabels={PAGINATION_LABELS}
                    onNextPageClick={({ detail }) => {
                        if (detail.requestedPageIndex > pagesCount && hasNextPage) {
                            onNextPage();
                        } else {
                            onPageChange(detail.requestedPageIndex);
                        }
                    }}
                    onPreviousPageClick={({ detail }) => {
                        onPreviousPage();
                        onPageChange(detail.requestedPageIndex);
                    }}
                    onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
                />
            }
            preferences={getTablePreferences(
                'Preferences',
                false,
                BULK_PROMOTION_HISTORY_PAGE_SIZE_OPTIONS,
                BULK_PROMOTION_HISTORY_VISIBLE_CONTENT_OPTIONS,
                preferences,
                onPreferencesChange
            )}
        />
    );
}
